import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CommunicationHistory from './components/CommunicationHistory';

function requestId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function EmailClient() {
    const token = localStorage.getItem('accessToken');
    const { caseId } = useParams();
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState('');
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
    const [documentReminder, setDocumentReminder] = useState(null);
    const [loadingDocumentReminder, setLoadingDocumentReminder] = useState(true);
    const [sendingDocumentReminder, setSendingDocumentReminder] = useState(false);
    const [documentReminderMessage, setDocumentReminderMessage] = useState('');
    const submissionLock = useRef(false);
    const documentReminderLock = useRef(false);

    const documentReminderUrl = `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/case/${caseId}/communications/post-filing-document-reminder/`;

    const loadDocumentReminder = useCallback(async () => {
      if (!caseId) return;
      setLoadingDocumentReminder(true);
      try {
        const response = await fetch(documentReminderUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || 'Could not load the document reminder.');
        setDocumentReminder(data);
      } catch (error) {
        setDocumentReminderMessage(`Error: ${error.message || 'Could not load the document reminder.'}`);
      } finally {
        setLoadingDocumentReminder(false);
      }
    }, [caseId, documentReminderUrl, token]);

    useEffect(() => {
      loadDocumentReminder();
    }, [loadDocumentReminder]);

    const resendRetainer = async () => {
      if (submissionLock.current) return;
      submissionLock.current = true;
      setSending(true);
      setMessage('');
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/case/${caseId}/resend-retainer/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ case_id: caseId, request_id: requestId() }),
        });
        if (response.ok) {
          setMessage('Getting Started email sent and added to the communication history.');
          setHistoryRefreshKey((current) => current + 1);
        } else {
          const data = await response.json();
          setMessage(`Error: ${data.detail || response.status}`);
        }
      } catch (err) {
        setMessage('Network error');
      } finally {
        submissionLock.current = false;
        setSending(false);
      }
    };

    const sendDocumentReminder = async () => {
      if (documentReminderLock.current) return;
      documentReminderLock.current = true;
      setSendingDocumentReminder(true);
      setDocumentReminderMessage('');
      try {
        const response = await fetch(documentReminderUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ request_id: requestId() }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || 'The document reminder could not be sent.');
        setDocumentReminder(data.context || documentReminder);
        setDocumentReminderMessage('Post-filing document reminder sent and added to communications history.');
        setHistoryRefreshKey((current) => current + 1);
      } catch (error) {
        setDocumentReminderMessage(`Error: ${error.message || 'The document reminder could not be sent.'}`);
      } finally {
        documentReminderLock.current = false;
        setSendingDocumentReminder(false);
      }
    };

    return (
      <div className="space-y-6">
        <section className="rounded-lg bg-white p-6 shadow">
          <h1 className="text-2xl font-bold text-gray-900">Communications</h1>
          <p className="mt-1 text-sm text-gray-600">Send case emails and review the complete email and text history.</p>
          <button
            type="button"
            onClick={resendRetainer}
            disabled={sending}
            className="mt-4 rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send Getting Started Email'}
          </button>
          {message && (
            <div className={`mt-4 rounded-md px-4 py-3 text-sm ${message.startsWith('Error') || message === 'Network error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {message}
            </div>
          )}

          <div className="mt-6 border-t border-gray-200 pt-5">
            <h2 className="text-lg font-semibold text-gray-900">Post-filing document reminder</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manually email the debtor a list of every outstanding post-filing requirement and a public upload link.
            </p>
            {loadingDocumentReminder ? (
              <p className="mt-3 text-sm text-gray-500">Checking outstanding documents…</p>
            ) : (
              <>
                {(documentReminder?.outstanding_documents || []).length > 0 ? (
                  <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 p-4">
                    <div className="text-sm font-semibold text-indigo-900">Outstanding documents</div>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-indigo-900">
                      {documentReminder.outstanding_documents.map((item) => (
                        <li key={item.id}>{item.name}</li>
                      ))}
                    </ul>
                    <div className="mt-3 text-xs text-indigo-800">
                      To: {(documentReminder.recipients || []).join(', ') || 'No debtor email available'}
                      <br />
                      From: {documentReminder.attorney
                        ? `${documentReminder.attorney.name} <${documentReminder.attorney.email}>`
                        : 'Firm email'}
                      <br />
                      CC: {(documentReminder.cc || []).join(', ') || 'None'}
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    No outstanding post-filing debtor documents.
                  </p>
                )}
                <button
                  type="button"
                  onClick={sendDocumentReminder}
                  disabled={!documentReminder?.can_send || sendingDocumentReminder}
                  className="mt-4 rounded bg-indigo-600 px-4 py-2 font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sendingDocumentReminder ? 'Sending…' : 'Send Document Reminder Now'}
                </button>
              </>
            )}
            {documentReminderMessage && (
              <div className={`mt-4 rounded-md px-4 py-3 text-sm ${documentReminderMessage.startsWith('Error:') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {documentReminderMessage}
              </div>
            )}
          </div>
        </section>
        <CommunicationHistory caseId={caseId} refreshKey={historyRefreshKey} />
      </div>
    );
  }
